import { useQuery } from "@tanstack/react-query";
import { inventory } from "@fm/api";
import type { ApiClient } from "@fm/api/types";
import type { UnavailableDateRange, DailyInventory } from "@fm/api";
import { getApiBaseUrl } from "../lib/config";
import { format, addMonths, startOfMonth, endOfMonth } from "date-fns";

export interface UseVehicleAvailabilityCalendarOptions {
  apiKey: string;
  vehicleId: string;
  fleetId: string;
  apiBaseUrl?: string;
  /** The base date for the calendar view (determines which months to fetch) */
  baseDate?: Date;
  /** Number of months to fetch (default: 2 for a two-month view) */
  monthsToFetch?: number;
  enabled?: boolean;
}

export interface CalendarDayData {
  date: string;
  available: boolean;
  rate: number | null;
  currency: string;
}

export interface UseVehicleAvailabilityCalendarReturn {
  /** Map of date (YYYY-MM-DD) to day data */
  calendarData: Map<string, CalendarDayData>;
  /** Array of unavailable date ranges with reasons */
  unavailableRanges: UnavailableDateRange[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useVehicleAvailabilityCalendar(
  options: UseVehicleAvailabilityCalendarOptions,
): UseVehicleAvailabilityCalendarReturn {
  const {
    apiKey,
    vehicleId,
    fleetId,
    apiBaseUrl,
    baseDate = new Date(),
    monthsToFetch = 2,
    enabled = true,
  } = options;

  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  // Calculate date range for the calendar view
  const fromDate = format(startOfMonth(baseDate), "yyyy-MM-dd");
  const toDate = format(
    endOfMonth(addMonths(baseDate, monthsToFetch - 1)),
    "yyyy-MM-dd",
  );

  // Fetch inventory grid for rates and availability
  const inventoryQuery = useQuery({
    queryKey: [
      "vehicleAvailabilityCalendar",
      "inventory",
      vehicleId,
      fleetId,
      fromDate,
      toDate,
    ],
    queryFn: async () => {
      const response = await inventory.getGrid(apiClient, {
        from_date: fromDate,
        to_date: toDate,
      });
      return response.data;
    },
    enabled: enabled && !!vehicleId && !!fleetId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch unavailable date ranges
  const unavailableQuery = useQuery({
    queryKey: [
      "vehicleAvailabilityCalendar",
      "unavailable",
      vehicleId,
      fromDate,
      toDate,
    ],
    queryFn: async () => {
      const response = await inventory.getUnavailableDateRanges(apiClient, {
        from_date: fromDate,
        to_date: toDate,
        vehicle_id: vehicleId,
      });
      return response.data || [];
    },
    enabled: enabled && !!vehicleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Build calendar data map
  const calendarData = new Map<string, CalendarDayData>();

  if (inventoryQuery.data?.vehicles) {
    // Find the specific vehicle in the grid
    // Note: The API returns public_id in the 'id' field of VehicleInventory
    const vehicleData = inventoryQuery.data.vehicles.find(
      (v) => v.id === vehicleId,
    );

    if (vehicleData?.daily_inventory) {
      vehicleData.daily_inventory.forEach((day: DailyInventory) => {
        calendarData.set(day.date, {
          date: day.date,
          available: day.available,
          rate: day.rate,
          currency: day.currency || "USD",
        });
      });
    }
  }

  return {
    calendarData,
    unavailableRanges: unavailableQuery.data || [],
    isLoading: inventoryQuery.isLoading || unavailableQuery.isLoading,
    isError: inventoryQuery.isError || unavailableQuery.isError,
    error: inventoryQuery.error || unavailableQuery.error,
    refetch: () => {
      inventoryQuery.refetch();
      unavailableQuery.refetch();
    },
  };
}
